import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import AuthGuard from "@/components/feature/AuthGuard";
import { useAuth } from "@/contexts/AuthContext";
import { useClaimedListings } from "@/hooks/useClaimedListings";
import { getSupabase } from "@/lib/supabase";
import type { HackerProfile } from "@/hooks/useHackers";

function LoadingState() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#161b22]">
      <div className="text-center">
        <i className="fas fa-spinner fa-spin text-3xl text-[#5bc0de] mb-3 block" />
        <p className="text-sm text-gray-500">Loading your dashboard...</p>
      </div>
    </div>
  );
}

function EditListingForm({ listing, onSave, onCancel }: {
  listing: HackerProfile;
  onSave: (data: Partial<HackerProfile>) => Promise<void>;
  onCancel: () => void;
}) {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: listing.name,
    specialty: listing.specialty,
    rate: listing.rate,
    experience: listing.experience,
    country: listing.country,
    availability: listing.availability || "",
    skills: listing.skills?.join(", ") || "",
    certifications: listing.certifications?.join(", ") || "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave({
        ...formData,
        skills: formData.skills.split(",").map((s) => s.trim()).filter(Boolean),
        certifications: formData.certifications.split(",").map((s) => s.trim()).filter(Boolean),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold text-gray-400 mb-1">Display Name</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
            className="w-full px-3 py-2.5 border border-gray-700 rounded text-sm focus:outline-none focus:border-[#5bc0de]"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-400 mb-1">Specialty</label>
          <input
            type="text"
            value={formData.specialty}
            onChange={(e) => setFormData({ ...formData, specialty: e.target.value })}
            required
            className="w-full px-3 py-2.5 border border-gray-700 rounded text-sm focus:outline-none focus:border-[#5bc0de]"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="block text-xs font-semibold text-gray-400 mb-1">Hourly Rate</label>
          <input
            type="text"
            value={formData.rate}
            onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
            required
            placeholder="$100/hr"
            className="w-full px-3 py-2.5 border border-gray-700 rounded text-sm focus:outline-none focus:border-[#5bc0de]"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-400 mb-1">Experience</label>
          <input
            type="text"
            value={formData.experience}
            onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
            required
            placeholder="5+ years"
            className="w-full px-3 py-2.5 border border-gray-700 rounded text-sm focus:outline-none focus:border-[#5bc0de]"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-400 mb-1">Country</label>
          <input
            type="text"
            value={formData.country}
            onChange={(e) => setFormData({ ...formData, country: e.target.value })}
            required
            className="w-full px-3 py-2.5 border border-gray-700 rounded text-sm focus:outline-none focus:border-[#5bc0de]"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-400 mb-1">Availability</label>
        <input
          type="text"
          value={formData.availability}
          onChange={(e) => setFormData({ ...formData, availability: e.target.value })}
          placeholder="Available 24/7"
          className="w-full px-3 py-2.5 border border-gray-700 rounded text-sm focus:outline-none focus:border-[#5bc0de]"
        />
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-400 mb-1">Skills (comma-separated)</label>
        <textarea
          value={formData.skills}
          onChange={(e) => setFormData({ ...formData, skills: e.target.value })}
          rows={2}
          className="w-full px-3 py-2.5 border border-gray-700 rounded text-sm focus:outline-none focus:border-[#5bc0de] resize-none"
        />
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-400 mb-1">Certifications (comma-separated)</label>
        <textarea
          value={formData.certifications}
          onChange={(e) => setFormData({ ...formData, certifications: e.target.value })}
          rows={2}
          className="w-full px-3 py-2.5 border border-gray-700 rounded text-sm focus:outline-none focus:border-[#5bc0de] resize-none"
        />
      </div>

      <div className="flex gap-3 pt-2">
        <button
          type="submit"
          disabled={saving}
          className="bg-[#5cb85c] hover:bg-[#4cae4c] disabled:bg-gray-700 disabled:cursor-not-allowed text-white text-sm font-semibold px-6 py-2.5 rounded transition-all whitespace-nowrap"
        >
          {saving ? <span><i className="fas fa-spinner fa-spin mr-2" />Saving...</span> : <span><i className="fas fa-check mr-2" />Save Changes</span>}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="border border-gray-600 text-gray-400 text-sm font-semibold px-6 py-2.5 rounded hover:bg-[#161b22] transition-all whitespace-nowrap"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

export default function DashboardPage() {
  const { user, profile, refreshProfile } = useAuth();
  const { listings, loading, error, refetch } = useClaimedListings(user?.id);

  return (
    <AuthGuard>
      <DashboardContent
        user={user}
        profile={profile}
        refreshProfile={refreshProfile}
        listings={listings}
        loading={loading}
        error={error}
        refetch={refetch}
      />
    </AuthGuard>
  );
}

function DashboardContent({
  user,
  profile,
  refreshProfile,
  listings,
  loading,
  error,
  refetch,
}: {
  user: { email?: string } | null;
  profile: { display_name?: string | null; username?: string | null; role?: string } | null;
  refreshProfile: () => Promise<void>;
  listings: HackerProfile[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}) {
  const { emailConfirmed, resendConfirmation } = useAuth();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saveStatus, setSaveStatus] = useState<"idle" | "success" | "error">("idle");
  const [saveMessage, setSaveMessage] = useState("");
  const [resendStatus, setResendStatus] = useState<"idle" | "loading" | "sent">("idle");
  const [editingProfile, setEditingProfile] = useState(false);
  const [displayNameInput, setDisplayNameInput] = useState("");
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileStatus, setProfileStatus] = useState<"idle" | "success" | "error">("idle");
  const [profileMessage, setProfileMessage] = useState("");

  // User's projects
  const [userProjects, setUserProjects] = useState<any[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(true);

  useEffect(() => {
    if (user?.id) {
      fetchUserProjects();
    }
  }, [user?.id]);

  const fetchUserProjects = async () => {
    setProjectsLoading(true);
    try {
      const supabase = getSupabase();
      const { data } = await supabase
        .from("projects")
        .select("*")
        .eq("client_id", user?.id)
        .order("created_at", { ascending: false });
      setUserProjects(data || []);
    } catch {
      setUserProjects([]);
    } finally {
      setProjectsLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    if (!user?.email) return;
    setResendStatus("loading");
    const { error } = await resendConfirmation(user.email);
    if (error) {
      setResendStatus("idle");
    } else {
      setResendStatus("sent");
    }
  };

  const handleSave = async (listingId: number, data: Partial<HackerProfile>) => {
    setSaveStatus("idle");
    try {
      const supabase = getSupabase();
      const { error: updateError } = await supabase
        .from("hacker_listings")
        .update(data)
        .eq("id", listingId)
        .eq("profile_id", user?.id);

      if (updateError) throw updateError;

      setSaveStatus("success");
      setSaveMessage("Profile updated successfully!");
      setEditingId(null);
      refetch();
      setTimeout(() => setSaveStatus("idle"), 3000);
    } catch (err) {
      setSaveStatus("error");
      setSaveMessage(err instanceof Error ? err.message : "Failed to save changes.");
    }
  };

  const handleEditProfileClick = () => {
    setDisplayNameInput(profile?.display_name || profile?.username || "");
    setEditingProfile(true);
    setProfileStatus("idle");
  };

  const handleCancelProfileEdit = () => {
    setEditingProfile(false);
    setProfileStatus("idle");
  };

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id) return;
    setProfileSaving(true);
    setProfileStatus("idle");
    try {
      const supabase = getSupabase();
      const { error: updateError } = await supabase
        .from("profiles")
        .update({ display_name: displayNameInput.trim() })
        .eq("id", user.id);

      if (updateError) throw updateError;

      await refreshProfile();
      setProfileStatus("success");
      setProfileMessage("Display name updated!");
      setEditingProfile(false);
      setTimeout(() => setProfileStatus("idle"), 3000);
    } catch (err) {
      setProfileStatus("error");
      setProfileMessage(err instanceof Error ? err.message : "Failed to update display name.");
    } finally {
      setProfileSaving(false);
    }
  };

  const reviewBadge = (status: string) => {
    switch (status) {
      case "approved": return "bg-green-900/30 text-green-400";
      case "rejected": return "bg-red-900/30 text-red-400";
      case "pending": return "bg-amber-900/30 text-amber-400";
      default: return "bg-[#1c2128] text-gray-400";
    }
  };

  const getPaymentIcon = (method: string | null | undefined): string => {
    switch (method) {
      case "crypto": return "fa-bitcoin";
      case "escrow": return "fa-shield-halved";
      case "wire": return "fa-building-columns";
      default: return "fa-credit-card";
    }
  };

  const formatPaymentMethod = (method: string | null | undefined): string => {
    if (!method) return "Not specified";
    if (method === "crypto") return "Crypto";
    if (method === "escrow") return "Escrow";
    if (method === "wire") return "Wire Transfer";
    return method;
  };

  if (loading) return <LoadingState />;

  return (
    <div className="min-h-screen bg-[#161b22]">
      <Header />
      <main className="pt-20 md:pt-24">
        <section className="bg-[#2c3e50] py-8 md:py-12">
          <div className="w-full px-4 md:px-8 max-w-5xl mx-auto">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-white">
                  {profile?.username || profile?.display_name || "Hacker"} Dashboard
                </h1>
                <p className="text-sm text-white/60 mt-1">
                  {profile?.role === "hacker" ? "Hacker" : "Member"} · {user?.email}
                </p>
              </div>
              <Link
                to="/hackers"
                className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors bg-[#0d1117]/10 hover:bg-[#0d1117]/20 px-4 py-2 rounded whitespace-nowrap"
              >
                <i className="fas fa-search" /> Browse Hackers Directory
              </Link>
            </div>
          </div>
        </section>

        <div className="w-full px-4 md:px-8 max-w-5xl mx-auto py-8">
          {/* Email Verification Banner */}
          {!emailConfirmed && (
            <div className="mb-6 p-4 bg-amber-900/20 border border-amber-700/30 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-amber-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="fas fa-envelope text-amber-400 text-sm" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-amber-400 mb-1">Email not confirmed</p>
                  <p className="text-xs text-amber-400 leading-relaxed mb-3">
                    We sent a confirmation link to <strong>{user?.email}</strong>. Please check your inbox and click the link to verify your account.
                  </p>
                  <button
                    type="button"
                    onClick={handleResendConfirmation}
                    disabled={resendStatus !== "idle"}
                    className="text-xs font-semibold text-amber-400 hover:text-amber-900 underline transition-colors disabled:opacity-50 whitespace-nowrap"
                  >
                    {resendStatus === "loading" ? (
                      <span><i className="fas fa-spinner fa-spin mr-1" />Sending...</span>
                    ) : resendStatus === "sent" ? (
                      <span><i className="fas fa-check mr-1" />Resent! Check your inbox</span>
                    ) : (
                      <span>Resend confirmation email</span>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {saveStatus === "success" && (
            <div className="mb-6 p-4 bg-green-900/20 border border-green-700/30 rounded text-sm text-green-400">
              <i className="fas fa-check-circle mr-2" />{saveMessage}
            </div>
          )}
          {saveStatus === "error" && (
            <div className="mb-6 p-4 bg-red-900/20 border border-red-700/30 rounded text-sm text-red-400">
              <i className="fas fa-exclamation-circle mr-2" />{saveMessage}
            </div>
          )}

          {profileStatus === "success" && (
            <div className="mb-6 p-4 bg-green-900/20 border border-green-700/30 rounded text-sm text-green-400">
              <i className="fas fa-check-circle mr-2" />{profileMessage}
            </div>
          )}
          {profileStatus === "error" && (
            <div className="mb-6 p-4 bg-red-900/20 border border-red-700/30 rounded text-sm text-red-400">
              <i className="fas fa-exclamation-circle mr-2" />{profileMessage}
            </div>
          )}

          {error && (
            <div className="mb-6 p-4 bg-red-900/20 border border-red-700/30 rounded text-sm text-red-400">
              <i className="fas fa-exclamation-circle mr-2" />{error}
            </div>
          )}

          {/* Stats row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-[#0d1117] rounded border border-gray-700 p-5">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Listings</p>
              <p className="text-2xl font-bold text-gray-200">{listings.length}</p>
            </div>
            <div className="bg-[#0d1117] rounded border border-gray-700 p-5">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Projects</p>
              <p className="text-2xl font-bold text-gray-200">{userProjects.length}</p>
            </div>
            <div className="bg-[#0d1117] rounded border border-gray-700 p-5">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Approved</p>
              <p className="text-2xl font-bold text-green-400">{userProjects.filter((p) => p.review_status === "approved").length}</p>
            </div>
            <div className="bg-[#0d1117] rounded border border-gray-700 p-5">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Pending</p>
              <p className="text-2xl font-bold text-amber-400">{userProjects.filter((p) => p.review_status === "pending").length}</p>
            </div>
          </div>

          {/* My Projects Section */}
          <h2 className="text-lg font-semibold text-gray-200 mb-4">My Projects</h2>

          {projectsLoading ? (
            <div className="flex items-center justify-center py-8">
              <i className="fas fa-spinner fa-spin text-[#5cb85c] text-lg" />
            </div>
          ) : userProjects.length === 0 ? (
            <div className="bg-[#0d1117] rounded border border-gray-700 p-10 text-center mb-8">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-[#1c2128]">
                <i className="fas fa-folder-open text-2xl text-gray-400" />
              </div>
              <h3 className="text-base font-semibold text-gray-300 mb-2">No Projects Yet</h3>
              <p className="text-sm text-gray-500 mb-6 max-w-md mx-auto">
                You haven&apos;t posted any projects yet. Submit a project to get started.
              </p>
              <Link
                to="/post-project"
                className="inline-block bg-[#5cb85c] hover:bg-[#4cae4c] text-white text-sm font-semibold px-6 py-2.5 rounded transition-all whitespace-nowrap"
              >
                <i className="fas fa-plus-circle mr-2" />Post a Project
              </Link>
            </div>
          ) : (
            <div className="space-y-4 mb-8">
              {userProjects.map((project) => (
                <div key={project.id} className="bg-[#0d1117] rounded border border-gray-700 p-5">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-gray-200 mb-1 truncate">{project.title}</h3>
                      <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                        <span><i className="fas fa-tag mr-1" />{project.category || "N/A"}</span>
                        <span className="font-semibold text-[#5cb85c] text-sm">{project.budget || "N/A"}</span>
                        <span className="flex items-center gap-1">
                          <i className={`fas ${getPaymentIcon(project.payment_method)} text-gray-400`} />
                          <span className="capitalize">{formatPaymentMethod(project.payment_method)}</span>
                        </span>
                        <span>
                          {project.created_at ? new Date(project.created_at).toLocaleDateString() : "—"}
                        </span>
                      </div>
                      {project.delivery_time && (
                        <div className="mt-1.5">
                          <span className="text-[10px] text-gray-600 flex items-center gap-1">
                            <i className="far fa-clock" />
                            {project.delivery_time}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full capitalize ${reviewBadge(project.review_status)}`}>
                        {project.review_status || "pending"}
                      </span>
                      <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full capitalize ${
                        project.status === "open" ? "bg-green-900/30 text-green-400" :
                        project.status === "completed" ? "bg-blue-100 text-blue-700" :
                        "bg-[#1c2128] text-gray-400"
                      }`}>
                        {(project.status || "open").replace("_", " ")}
                      </span>
                      <Link
                        to={`/project/${project.id}`}
                        className="text-xs text-[#5bc0de] hover:text-[#46b8da] font-semibold whitespace-nowrap"
                      >
                        <i className="fas fa-external-link-alt mr-1" />View
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Info Banner */}
          <div className="bg-[#5bc0de]/10 border border-[#5bc0de]/20 rounded p-4 mb-8">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-[#5bc0de]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="fas fa-info-circle text-[#5bc0de] text-sm" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-200 mb-1">How It Works</p>
                <p className="text-xs text-gray-400 leading-relaxed">
                  Clients submit requests through our platform. Our concierge team reviews each request and assigns projects to the best-matched hackers. 
                  You do not receive messages directly — the team handles client communication and allocation. 
                  Keep your profile up to date for the best matching results.
                </p>
              </div>
            </div>
          </div>

          {/* Listings */}
          <h2 className="text-lg font-semibold text-gray-200 mb-4">Your Listings</h2>

          {listings.length === 0 ? (
            <div className="bg-[#0d1117] rounded border border-gray-700 p-10 text-center">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-[#1c2128]">
                <i className="fas fa-user-secret text-2xl text-gray-400" />
              </div>
              <h3 className="text-base font-semibold text-gray-300 mb-2">No Listings Yet</h3>
              <p className="text-sm text-gray-500 mb-6 max-w-md mx-auto">
                You haven&apos;t claimed any hacker profiles yet. Browse the hackers directory and claim your profile to get started.
              </p>
              <Link
                to="/hackers"
                className="inline-block bg-[#5cb85c] hover:bg-[#4cae4c] text-white text-sm font-semibold px-6 py-2.5 rounded transition-all whitespace-nowrap"
              >
                <i className="fas fa-search mr-2" />Find Your Profile
              </Link>
            </div>
          ) : (
            <div className="space-y-6">
              {listings.map((listing) => (
                <div key={listing.id} className="bg-[#0d1117] rounded border border-gray-700 overflow-hidden">
                  <div className="p-5 md:p-6 flex flex-col sm:flex-row items-start gap-4">
                    <img
                      src={listing.avatar}
                      alt={listing.name}
                      className="w-16 h-16 rounded-full object-cover flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="text-base font-semibold text-gray-200">{listing.name}</h3>
                        <span className="bg-[#5bc0de]/10 text-[#5bc0de] px-2 py-0.5 rounded text-[10px] font-semibold">{listing.specialty}</span>
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 mb-3">
                        <span><i className="fas fa-star text-yellow-400 mr-1" />{listing.rating?.toFixed(1)}</span>
                        <span><i className="fas fa-map-marker-alt mr-1" />{listing.country}</span>
                        <span className="font-semibold text-[#5cb85c]">{listing.rate}</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {listing.skills?.map((s) => (
                          <span key={s} className="text-[10px] bg-[#1c2128] text-gray-400 px-2 py-0.5 rounded">{s}</span>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Link
                        to={`/hacker/${listing.id}`}
                        className="text-xs text-[#5bc0de] hover:text-[#46b8da] font-semibold px-3 py-1.5 border border-[#5bc0de]/30 rounded hover:bg-[#5bc0de]/5 transition-all whitespace-nowrap"
                      >
                        <i className="fas fa-external-link-alt mr-1" />View
                      </Link>
                      {editingId === listing.id ? (
                        <button
                          onClick={() => setEditingId(null)}
                          className="text-xs text-gray-500 hover:text-gray-300 font-semibold px-3 py-1.5 border border-gray-600 rounded hover:bg-[#161b22] transition-all whitespace-nowrap"
                        >
                          Cancel Edit
                        </button>
                      ) : (
                        <button
                          onClick={() => setEditingId(listing.id)}
                          className="text-xs text-gray-300 font-semibold px-3 py-1.5 border border-gray-600 rounded hover:bg-[#161b22] transition-all whitespace-nowrap"
                        >
                          <i className="fas fa-edit mr-1" />Edit
                        </button>
                      )}
                    </div>
                  </div>

                  {editingId === listing.id && (
                    <div className="border-t border-gray-800 p-5 md:p-6 bg-[#161b22]/50">
                      <EditListingForm
                        listing={listing}
                        onSave={(data) => handleSave(listing.id, data)}
                        onCancel={() => setEditingId(null)}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Account Section */}
          <div className="mt-10 bg-[#0d1117] rounded border border-gray-700 p-5 md:p-6">
            <h2 className="text-lg font-semibold text-gray-200 mb-4">Account Settings</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between py-3 border-b border-gray-800">
                <div>
                  <p className="text-sm font-medium text-gray-300">Email Address</p>
                  <p className="text-xs text-gray-500">{user?.email}</p>
                </div>
                <span className="text-xs text-gray-400">Cannot be changed</span>
              </div>
              <div className="flex items-center justify-between py-3 border-b border-gray-800">
                <div>
                  <p className="text-sm font-medium text-gray-300">Account Role</p>
                  <p className="text-xs text-gray-500">{profile?.role === "admin" ? "Administrator" : "Client"}</p>
                </div>
                <span className="text-[10px] bg-[#5bc0de]/10 text-[#5bc0de] px-2 py-0.5 rounded uppercase font-semibold">{profile?.role === "admin" ? "Admin" : "Client"}</span>
              </div>
              <div className="py-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-300">Display Name</p>
                    {!editingProfile && (
                      <p className="text-xs text-gray-500">{profile?.display_name || profile?.username || "Not set"}</p>
                    )}
                  </div>
                  {!editingProfile ? (
                    <button
                      type="button"
                      onClick={handleEditProfileClick}
                      className="text-xs text-[#5bc0de] hover:text-[#46b8da] font-semibold px-3 py-1.5 border border-[#5bc0de]/30 rounded hover:bg-[#5bc0de]/5 transition-all whitespace-nowrap"
                    >
                      <i className="fas fa-edit mr-1" />Edit
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handleCancelProfileEdit}
                      className="text-xs text-gray-500 hover:text-gray-300 font-semibold px-3 py-1.5 border border-gray-600 rounded hover:bg-[#161b22] transition-all whitespace-nowrap"
                    >
                      Cancel
                    </button>
                  )}
                </div>
                {editingProfile && (
                  <form onSubmit={handleSaveProfile} className="mt-3 flex flex-col sm:flex-row items-start sm:items-center gap-3">
                    <input
                      type="text"
                      value={displayNameInput}
                      onChange={(e) => setDisplayNameInput(e.target.value)}
                      required
                      maxLength={50}
                      placeholder="Enter your display name"
                      className="flex-1 w-full sm:w-auto px-3 py-2.5 border border-gray-700 rounded text-sm focus:outline-none focus:border-[#5bc0de] bg-[#161b22] text-gray-200"
                    />
                    <button
                      type="submit"
                      disabled={profileSaving || !displayNameInput.trim()}
                      className="bg-[#5cb85c] hover:bg-[#4cae4c] disabled:bg-gray-700 disabled:cursor-not-allowed text-white text-sm font-semibold px-4 py-2.5 rounded transition-all whitespace-nowrap"
                    >
                      {profileSaving ? (
                        <span><i className="fas fa-spinner fa-spin mr-1" />Saving...</span>
                      ) : (
                        <span><i className="fas fa-check mr-1" />Save</span>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}